import { useState, useEffect, useCallback } from 'react';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Cell } from 'recharts';
import api from '../api/client';
import InfoBubble from '../components/common/InfoBubble';

// ── Design tokens ─────────────────────────────────────────────────────────
const C = {
  ivory: '#FAF7F2', cream: '#F3EDE4', parchment: '#EBE3D6',
  navy: '#1B2A4A', navyLight: '#2A3D66', navyMuted: '#3A5080',
  burgundy: '#7A2E3B', burgundyMuted: 'rgba(122,46,59,0.12)',
  gold: '#B8953E', goldLight: '#D4AF5C', goldMuted: 'rgba(184,149,62,0.15)',
  hunter: '#2D5F4A', hunterMuted: 'rgba(45,95,74,0.12)',
  ink: '#1A1A1A', textBody: '#3D3830', textMuted: '#7A7265', textLight: '#A69D91',
  border: '#D6CCBE', borderLight: '#E8E0D4', borderDark: '#C4B8A8',
};
const fonts = {
  display: "'Cormorant Garamond', Georgia, serif",
  body:    "'Libre Baskerville', Georgia, serif",
  mono:    "'IBM Plex Mono', 'Courier New', monospace",
  sans:    "'Lato', 'Helvetica Neue', sans-serif",
};

// ── Shared UI ─────────────────────────────────────────────────────────────
const Card = ({ children, style: s = {} }: { children: React.ReactNode; style?: React.CSSProperties }) => (
  <div style={{ background: C.ivory, border: `1px solid ${C.border}`, borderRadius: 3, ...s }}>{children}</div>
);
const PageHeader = ({ title, subtitle }: { title: string; subtitle: string }) => (
  <div style={{ marginBottom: 28, borderBottom: `1px solid ${C.border}`, paddingBottom: 16 }}>
    <h1 style={{ fontSize: 28, fontFamily: fonts.display, fontWeight: 600, color: C.ink, margin: 0 }}>{title}</h1>
    <p style={{ fontSize: 14, fontFamily: fonts.body, color: C.textMuted, margin: '6px 0 0', fontStyle: 'italic' }}>{subtitle}</p>
  </div>
);
const SectionTitle = ({ children, sub, info }: { children: React.ReactNode; sub?: string; info?: string }) => (
  <div style={{ marginBottom: 18 }}>
    <h2 style={{ fontSize: 13, fontWeight: 700, color: C.textMuted, fontFamily: fonts.sans, letterSpacing: '0.1em', textTransform: 'uppercase', margin: 0, borderBottom: `2px solid ${C.navy}`, display: 'inline-flex', alignItems: 'center', paddingBottom: 4 }}>{children}{info && <InfoBubble term={info} />}</h2>
    {sub && <p style={{ fontSize: 13, color: C.textLight, margin: '6px 0 0', fontFamily: fonts.body, fontStyle: 'italic' }}>{sub}</p>}
  </div>
);
const Stat = ({ label, value, sub, accent, small, info }: { label: string; value: string; sub?: string; accent?: string; small?: boolean; info?: string }) => (
  <div style={{ padding: small ? '14px 16px' : '18px 22px', borderRight: `1px solid ${C.borderLight}`, flex: 1, minWidth: small ? 110 : 140 }}>
    <div style={{ fontSize: 10, color: C.textLight, letterSpacing: '0.1em', fontFamily: fonts.sans, fontWeight: 700, marginBottom: 8, textTransform: 'uppercase', display: 'flex', alignItems: 'center' }}>{label}{info && <InfoBubble term={info} />}</div>
    <div style={{ fontSize: small ? 18 : 24, fontWeight: 600, color: accent || C.ink, fontFamily: fonts.display, lineHeight: 1 }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: C.textMuted, marginTop: 6, fontFamily: fonts.sans }}>{sub}</div>}
  </div>
);
const AlertBox = ({ type, title, children, info }: { type: string; title?: string; children: React.ReactNode; info?: string }) => {
  const s: Record<string, any> = {
    danger:  { bg: C.burgundyMuted, border: C.burgundy, fg: C.burgundy },
    warning: { bg: C.goldMuted,     border: C.gold,     fg: C.gold },
    success: { bg: C.hunterMuted,   border: C.hunter,   fg: C.hunter },
    info:    { bg: 'rgba(42,61,102,0.08)', border: C.navy, fg: C.navy },
  };
  const t = s[type] ?? s.info;
  return (
    <div style={{ padding: '14px 18px', background: t.bg, borderRadius: 3, borderLeft: `3px solid ${t.border}`, marginBottom: 10 }}>
      {title && <div style={{ fontFamily: fonts.sans, fontSize: 13, fontWeight: 700, color: t.fg, marginBottom: 4, display: 'flex', alignItems: 'center' }}>{title}{info && <InfoBubble term={info} />}</div>}
      <div style={{ fontFamily: fonts.body, fontSize: 12, color: C.textBody, lineHeight: 1.6 }}>{children}</div>
    </div>
  );
};
const StatusPill = ({ status, label }: { status: string; label: string }) => {
  const m: Record<string, any> = {
    current: { bg: C.hunterMuted,   fg: C.hunter },
    watch:   { bg: C.goldMuted,     fg: C.gold },
    danger:  { bg: C.burgundyMuted, fg: C.burgundy },
    none:    { bg: C.cream,         fg: C.textLight },
  };
  const t = m[status] ?? m.none;
  return <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 2, fontSize: 10, fontWeight: 700, fontFamily: fonts.sans, letterSpacing: '0.06em', textTransform: 'uppercase', background: t.bg, color: t.fg }}>{label}</span>;
};
const TH = ({ children, align = 'left' }: { children?: React.ReactNode; align?: string }) => (
  <th style={{ fontSize: 10, fontWeight: 700, color: C.textLight, fontFamily: fonts.sans, letterSpacing: '0.08em', textTransform: 'uppercase', textAlign: align as any, padding: '10px 14px', borderBottom: `2px solid ${C.borderDark}`, background: C.cream }}>{children}</th>
);
const TD = ({ children, mono, accent, align = 'left', bold }: { children?: React.ReactNode; mono?: boolean; accent?: string; align?: string; bold?: boolean }) => (
  <td style={{ fontSize: 13, color: accent || C.textBody, padding: '11px 14px', borderBottom: `1px solid ${C.borderLight}`, fontFamily: mono ? fonts.mono : fonts.sans, fontWeight: bold ? 600 : 400, textAlign: align as any }}>{children}</td>
);
const ChartTip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: C.ivory, border: `1px solid ${C.border}`, borderRadius: 4, padding: '10px 14px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', fontFamily: fonts.sans, fontSize: 12 }}>
      <p style={{ color: C.textMuted, margin: '0 0 6px', fontSize: 11, fontWeight: 600 }}>{label}</p>
      {payload.map((e: any, i: number) => (
        <p key={i} style={{ color: e.color || C.ink, margin: '2px 0', fontWeight: 500 }}>{e.name}: {fmtM(e.value)}</p>
      ))}
    </div>
  );
};

function fmtM(v: number | null | undefined): string {
  if (v == null) return '—';
  const abs = Math.abs(v); const neg = v < 0;
  const w = (s: string) => neg ? `(${s})` : s;
  if (abs >= 1e6) return w(`$${(abs / 1e6).toFixed(1)}M`);
  if (abs >= 1e3) return w(`$${(abs / 1e3).toFixed(0)}K`);
  return w(`$${abs.toFixed(0)}`);
}
function fmtPct(v: number): string { return `${v.toFixed(1)}%`; }

// ── Types ─────────────────────────────────────────────────────────────────
interface StateExposure {
  stateCode: string; stateName: string; situsValue: number; tax: number;
  taxType: string; isCliff: boolean; cliffSavings: number; belowExemption: boolean;
  exemption?: number; topRate?: number; notes: string;
}
interface Summary {
  inputs: { grossEstate: number; debtAtDeath: number; taxableEstate: number; exemption: number; domicile: string; };
  federal: { tax: number; exemption: number; taxableEstate: number; effectiveRate: number; };
  state: { exposures: StateExposure[]; totalStateTax: number; };
  interaction: { sdtdBenefit: number; netCombinedTax: number; effectiveCombinedRate: number; };
  sec6166: { eligible: boolean; pct: number; reason: string; };
  cliffWarnings: any[];
}
interface TimingRow {
  deathYear: number; label: string; grossEstate: number; debtDeduction: number;
  taxableEstate: number; fedTax: number; stateTax: number; combinedTax: number;
  liquidity: number; gap: number; sec6166Eligible: boolean;
}

// ── Main page ─────────────────────────────────────────────────────────────
export default function EstateTax() {
  const [summary, setSummary]     = useState<Summary | null>(null);
  const [timing, setTiming]       = useState<TimingRow[]>([]);
  const [scenarioMode, setScenarioMode] = useState<'current' | 'lower'>('current');
  const [stateTab, setStateTab]   = useState<'overview' | 'detail' | 'interaction'>('overview');
  const [horizon, setHorizon]     = useState<number>(6);
  const [loading, setLoading]     = useState(true);
  const [noData, setNoData]       = useState(false);

  const isLowerExemption = scenarioMode === 'lower';

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, timRes] = await Promise.all([
        api.get(`/estate/summary?sunset=${isLowerExemption}`),
        api.get(`/estate/timing?sunset=${isLowerExemption}&horizon=${horizon}`),
      ]);
      const s: Summary = sumRes.data;
      setSummary(s);
      setTiming(timRes.data.timing ?? []);
      setNoData(s.inputs.grossEstate === 0);
    } catch (e) {
      console.error('Estate fetch error', e);
    } finally {
      setLoading(false);
    }
  }, [isLowerExemption, horizon]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
      <span style={{ fontFamily: fonts.sans, fontSize: 13, color: C.textMuted }}>Computing estate tax exposure…</span>
    </div>
  );

  const statesWithTax = summary?.state.exposures.filter(e => e.tax > 0) ?? [];
  const cliffs = summary?.cliffWarnings ?? [];

  // Chart data for timing overlay
  const timingChartData = timing.map(t => ({
    label: t.label, fedTax: t.fedTax, stateTax: t.stateTax,
    liquidity: t.liquidity, gap: t.gap,
  }));

  // Federal-state interaction chart
  const interactionData = summary ? [
    { label: 'Federal Tax',    value: summary.federal.tax,                   color: C.burgundy },
    { label: 'State Tax',      value: summary.state.totalStateTax,           color: C.navyMuted },
    { label: 'SDTD Offset',    value: -summary.interaction.sdtdBenefit,      color: C.hunter },
    { label: 'Net Combined',   value: summary.interaction.netCombinedTax,    color: C.ink },
  ] : [];

  return (
    <div>
      <PageHeader title="Estate Tax Analysis" subtitle="Combined federal and state exposure with timing and legislative scenario modeling" />

      {/* ── KPI bar ── */}
      <Card style={{ display: 'flex', marginBottom: 24, overflow: 'hidden', flexWrap: 'wrap' }}>
        <Stat label="Gross Estate"      value={fmtM(summary?.inputs.grossEstate)}     sub="Total asset value"                            small />
        <Stat label="Federal Exclusion" value={fmtM(summary?.federal.exemption)}      sub={isLowerExemption ? 'Reduced scenario' : 'Current law (OBBBA)'}  accent={isLowerExemption ? C.burgundy : C.ink} small info="OBBBA" />
        <Stat label="States w/ Exposure" value={String(statesWithTax.length)}          sub={statesWithTax.map(s => s.stateCode).join(' · ') || 'None detected'} accent={C.navy} small />
        <Stat label="Federal Tax"       value={fmtM(summary?.federal.tax)}             sub={`${fmtPct(summary?.federal.effectiveRate ?? 0)} of taxable estate`}    accent={C.burgundy} small />
        <Stat label="State Tax"         value={fmtM(summary?.state.totalStateTax)}     sub="All jurisdictions"                            accent={C.navyMuted} small />
        <Stat label="Net Combined"      value={fmtM(summary?.interaction.netCombinedTax)} sub={`${fmtPct(summary?.interaction.effectiveCombinedRate ?? 0)} effective rate`} accent={C.burgundy} small />
      </Card>

      {/* ── Time horizon selector ── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
        <span style={{ fontFamily: fonts.sans, fontSize: 11, fontWeight: 700, color: C.textMuted, letterSpacing: '0.08em', textTransform: 'uppercase', marginRight: 4 }}>Time Horizon</span>
        {[3, 6, 10, 15, 20].map(y => (
          <button key={y} onClick={() => setHorizon(y)} style={{
            padding: '6px 14px', borderRadius: 3, cursor: 'pointer', fontFamily: fonts.sans, fontSize: 12, fontWeight: 600,
            border: `1px solid ${horizon === y ? C.navy : C.border}`,
            background: horizon === y ? C.navy : C.ivory, color: horizon === y ? C.ivory : C.textMuted,
          }}>{y}Y</button>
        ))}
      </div>

      {/* ── Legislative scenario selector ── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, alignItems: 'center' }}>
        {[
          { id: 'current' as const, l: 'Current Law ($15M)' },
          { id: 'lower' as const, l: 'Reduced Exemption (~$7M)' },
        ].map(s => (
          <button key={s.id} onClick={() => setScenarioMode(s.id)} style={{
            padding: '8px 18px', borderRadius: 3, cursor: 'pointer', fontFamily: fonts.sans, fontSize: 12, fontWeight: 600,
            border: `1px solid ${scenarioMode === s.id ? C.navy : C.border}`,
            background: scenarioMode === s.id ? C.navy : C.ivory, color: scenarioMode === s.id ? C.ivory : C.textMuted,
          }}>{s.l}</button>
        ))}
        <InfoBubble term="OBBBA" />
      </div>

      {noData && (
        <AlertBox type="info" title="No Assets Entered">
          Add assets and liabilities in the data management section to generate estate tax projections.
        </AlertBox>
      )}

      {/* ── Cliff warnings ── */}
      {cliffs.length > 0 && cliffs.map((c: any, i: number) => (
        <AlertBox key={i} type="danger" title={`${c.stateName} — Cliff Warning`}>{c.notes}</AlertBox>
      ))}

      {/* ── §6166 status ── */}
      {summary && (
        <AlertBox type={summary.sec6166.eligible ? 'success' : 'warning'} title={`§6166 Installment Election — ${summary.sec6166.eligible ? 'Eligible' : 'Ineligible'}`} info="§6166">
          {summary.sec6166.reason}. {summary.sec6166.eligible ? 'Estate tax may be paid in installments over up to 14 years.' : 'Consider restructuring to reach 35% closely held threshold.'}
        </AlertBox>
      )}

      {/* ── Timing overlay chart ── */}
      {timing.length > 0 && (
        <Card style={{ padding: 24, marginBottom: 20 }}>
          <SectionTitle sub="Federal and state tax versus available liquidity at each modeled death year">Combined Tax vs Liquidity</SectionTitle>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={timingChartData} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.borderLight} />
              <XAxis dataKey="label" tick={{ fill: C.textLight, fontSize: 12, fontFamily: fonts.sans }} axisLine={{ stroke: C.border }} />
              <YAxis tick={{ fill: C.textLight, fontSize: 11, fontFamily: fonts.sans }} axisLine={{ stroke: C.border }} tickFormatter={v => `$${(v/1e6).toFixed(0)}M`} />
              <Tooltip content={<ChartTip />} />
              <Bar dataKey="fedTax"   stackId="tax" fill={C.burgundy}  name="Federal Tax"  barSize={34} fillOpacity={0.85} />
              <Bar dataKey="stateTax" stackId="tax" fill={C.navyMuted} name="State Tax"    barSize={34} fillOpacity={0.85} radius={[3,3,0,0]} />
              <Bar dataKey="liquidity" fill={C.hunter} name="Liquidity" barSize={34} fillOpacity={0.7} radius={[3,3,0,0]} />
              <Line type="monotone" dataKey="gap" stroke={C.gold} strokeWidth={2.5} dot={{ fill: C.gold, r: 5, strokeWidth: 0 }} name="Liquidity Gap" />
            </ComposedChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* ── State tax panel ── */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 4, marginBottom: 16, background: C.parchment, borderRadius: 3, padding: 3, width: 'fit-content' }}>
          {([['overview', 'State Overview'], ['detail', 'Exposure Detail'], ['interaction', 'Federal-State Interaction']] as const).map(([id, l]) => (
            <button key={id} onClick={() => setStateTab(id)} style={{
              padding: '7px 16px', borderRadius: 2, border: 'none', cursor: 'pointer',
              fontFamily: fonts.sans, fontSize: 11, fontWeight: 600,
              background: stateTab === id ? C.navy : 'transparent',
              color: stateTab === id ? C.ivory : C.textMuted,
            }}>{l}</button>
          ))}
        </div>

        {stateTab === 'overview' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <Card style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '20px 24px 12px' }}><SectionTitle sub="Jurisdictions with estate or inheritance tax exposure">Multi-State Exposure</SectionTitle></div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><TH>State</TH><TH>Type</TH><TH align="right">Situs Value</TH><TH align="right">Est. Tax</TH><TH>Status</TH></tr></thead>
                <tbody>
                  {(summary?.state.exposures ?? []).map((s, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : C.cream }}>
                      <TD bold>{s.stateName}</TD>
                      <TD>{s.taxType === 'none' ? 'No Tax' : s.taxType === 'estate' ? 'Estate Tax' : 'Inheritance'}</TD>
                      <TD mono align="right">{fmtM(s.situsValue)}</TD>
                      <TD mono align="right" accent={s.tax > 0 ? C.burgundy : C.hunter}>{s.tax > 0 ? fmtM(s.tax) : '$0'}</TD>
                      <TD>
                        <StatusPill
                          status={s.tax === 0 ? 'none' : s.isCliff ? 'danger' : 'watch'}
                          label={s.tax === 0 ? 'No Tax' : s.isCliff ? 'Cliff' : 'Taxable'}
                        />
                      </TD>
                    </tr>
                  ))}
                </tbody>
              </table>
              {summary && (
                <div style={{ padding: '14px 24px', background: C.cream, borderTop: `1px solid ${C.borderLight}`, display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontFamily: fonts.sans, fontSize: 12, fontWeight: 700, color: C.textBody }}>Total State Tax Exposure</span>
                  <span style={{ fontFamily: fonts.mono, fontSize: 14, fontWeight: 700, color: C.burgundy }}>{fmtM(summary.state.totalStateTax)}</span>
                </div>
              )}
            </Card>
            <Card style={{ padding: 24 }}>
              <SectionTitle>State Tax Alerts</SectionTitle>
              {summary?.state.exposures.filter(e => e.tax > 0).length === 0 && (
                <AlertBox type="success" title="No State Tax Exposure">No assets are located in estate tax or inheritance tax states. Domicile advantage is intact.</AlertBox>
              )}
              {summary?.state.exposures.filter(e => e.isCliff && e.tax > 0).map((e, i) => (
                <AlertBox key={i} type="danger" title={`${e.stateName} — Cliff`}>{e.notes}</AlertBox>
              ))}
              {summary?.state.exposures.filter(e => e.tax > 0 && !e.isCliff && e.taxType === 'estate').map((e, i) => (
                <AlertBox key={i} type="warning" title={`${e.stateName} — Graduated`}>
                  {fmtM(e.situsValue)} in {e.stateName} generates {fmtM(e.tax)} in state estate tax at up to {fmtPct((e.topRate ?? 0) * 100)} rate.
                </AlertBox>
              ))}
              {summary?.inputs.domicile && !STATE_ESTATE_TAX_CODES.includes(summary.inputs.domicile) && (
                <AlertBox type="success" title={`${summary.inputs.domicile} Domicile — No Death Tax`}>
                  Oklahoma imposes no state estate or inheritance tax. Intangible assets (securities, business interests) held here are shielded from state death tax.
                </AlertBox>
              )}
            </Card>
          </div>
        )}

        {stateTab === 'detail' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <Card style={{ padding: 24 }}>
              <SectionTitle sub="Asset situs creating state tax nexus">Situs Property by State</SectionTitle>
              {(summary?.state.exposures ?? []).map((e, i) => (
                <div key={i} style={{ marginBottom: 14, paddingBottom: 14, borderBottom: `1px solid ${C.borderLight}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontFamily: fonts.sans, fontSize: 13, fontWeight: 700, color: C.navy }}>{e.stateName}</span>
                    <span style={{ fontFamily: fonts.mono, fontSize: 13, color: C.ink, fontWeight: 600 }}>{fmtM(e.situsValue)}</span>
                  </div>
                  <div style={{ height: 4, background: C.parchment, borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{ height: '100%', borderRadius: 2, background: e.tax > 0 ? (e.isCliff ? C.burgundy : C.gold) : C.hunter,
                      width: `${Math.min(100, (e.situsValue / (summary?.inputs.grossEstate || 1)) * 100)}%` }} />
                  </div>
                  {e.notes && <div style={{ fontFamily: fonts.sans, fontSize: 11, color: e.isCliff ? C.burgundy : C.textMuted, marginTop: 4 }}>{e.notes}</div>}
                </div>
              ))}
            </Card>
            <Card style={{ padding: 24 }}>
              <SectionTitle>State Rate Reference</SectionTitle>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><TH>State</TH><TH>Type</TH><TH align="right">Exemption</TH><TH align="right">Top Rate</TH><TH>Structure</TH></tr></thead>
                <tbody>
                  {(summary?.state.exposures ?? []).filter(e => e.taxType !== 'none').map((e, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : C.cream }}>
                      <TD bold>{e.stateCode}</TD>
                      <TD>{e.taxType === 'estate' ? 'Estate' : 'Inheritance'}</TD>
                      <TD mono align="right">{e.exemption ? fmtM(e.exemption) : 'By class'}</TD>
                      <TD mono align="right">{e.topRate ? fmtPct(e.topRate * 100) : 'Varies'}</TD>
                      <TD><span style={{ color: e.isCliff ? C.burgundy : C.textBody, fontWeight: e.isCliff ? 700 : 400 }}>{e.isCliff ? 'Cliff ⚠' : 'Graduated'}</span></TD>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </div>
        )}

        {stateTab === 'interaction' && summary && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <Card style={{ padding: 24 }}>
              <SectionTitle sub="State death taxes reduce federal taxable estate via IRC §2058" info="§2058">Federal-State Interaction</SectionTitle>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={interactionData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke={C.borderLight} horizontal={false} />
                  <XAxis type="number" tick={{ fill: C.textLight, fontSize: 11, fontFamily: fonts.sans }} axisLine={{ stroke: C.border }} tickFormatter={v => fmtM(v)} />
                  <YAxis type="category" dataKey="label" tick={{ fill: C.textMuted, fontSize: 11, fontFamily: fonts.sans }} axisLine={{ stroke: C.border }} width={100} />
                  <Tooltip content={<ChartTip />} />
                  <Bar dataKey="value" name="Amount" barSize={18} radius={[0, 3, 3, 0]}>
                    {interactionData.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
            <Card style={{ padding: 24 }}>
              <SectionTitle>Interaction Summary</SectionTitle>
              <AlertBox type="info" title="State Death Tax Deduction (SDTD)" info="§2058">
                State estate and inheritance taxes paid are deductible on the federal return (IRC §2058), reducing the federal taxable estate. The SDTD benefit is approximately {fmtM(summary.interaction.sdtdBenefit)} — meaning the net cost of {fmtM(summary.state.totalStateTax)} in state taxes is effectively {fmtM(summary.state.totalStateTax - summary.interaction.sdtdBenefit)} after the federal offset.
              </AlertBox>
              <div style={{ background: C.cream, borderRadius: 3, padding: 18, marginTop: 12 }}>
                {[
                  { l: 'Gross estate',        v: fmtM(summary.inputs.grossEstate),          c: C.ink },
                  { l: 'Debt deduction',       v: `(${fmtM(summary.inputs.debtAtDeath)})`,   c: C.hunter },
                  { l: 'Taxable estate',       v: fmtM(summary.inputs.taxableEstate),         c: C.ink, bold: true },
                  { l: 'Federal estate tax',   v: fmtM(summary.federal.tax),                  c: C.burgundy },
                  { l: 'Total state tax',      v: fmtM(summary.state.totalStateTax),           c: C.navyMuted },
                  { l: 'SDTD federal offset',  v: `(${fmtM(summary.interaction.sdtdBenefit)})`, c: C.hunter },
                  { l: 'Net combined tax',     v: fmtM(summary.interaction.netCombinedTax),    c: C.ink, bold: true },
                  { l: 'Effective rate',       v: fmtPct(summary.interaction.effectiveCombinedRate), c: C.ink, bold: true },
                ].map((r, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: i < 7 ? `1px solid ${C.borderLight}` : 'none' }}>
                    <span style={{ fontFamily: fonts.sans, fontSize: 12, color: C.textBody, fontWeight: r.bold ? 700 : 400 }}>{r.l}</span>
                    <span style={{ fontFamily: fonts.mono, fontSize: 13, color: r.c, fontWeight: r.bold ? 700 : 600 }}>{r.v}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}
      </div>

      {/* ── Timing table ── */}
      {timing.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '20px 24px 12px' }}><SectionTitle info="§6166">Timing Scenario Detail</SectionTitle></div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><TH>Event</TH><TH align="right">Federal</TH><TH align="right">State</TH><TH align="right">Combined</TH><TH align="right">Gap</TH><TH>§6166</TH></tr></thead>
              <tbody>
                {timing.map((d, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : C.cream }}>
                    <TD bold>{d.label}</TD>
                    <TD mono align="right" accent={C.burgundy}>{fmtM(d.fedTax)}</TD>
                    <TD mono align="right" accent={C.navyMuted}>{fmtM(d.stateTax)}</TD>
                    <TD mono align="right">{fmtM(d.combinedTax)}</TD>
                    <TD mono align="right" accent={d.gap > 0 ? C.burgundy : C.hunter}>{fmtM(d.gap)}</TD>
                    <TD><StatusPill status={d.sec6166Eligible ? 'current' : 'danger'} label={d.sec6166Eligible ? 'Eligible' : 'Ineligible'} /></TD>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
          <Card style={{ padding: 24 }}>
            <SectionTitle>Planning Opportunities</SectionTitle>
            {summary && summary.interaction.netCombinedTax > 0 && (
              <AlertBox type="warning" title="Liquidity Gap" info="ILIT">
                Combined tax of {fmtM(summary.interaction.netCombinedTax)} may exceed available liquidity in early years. An ILIT with {fmtM(summary.interaction.netCombinedTax)} death benefit would fully fund the obligation outside the taxable estate.
              </AlertBox>
            )}
            {cliffs.length > 0 && (
              <AlertBox type="danger" title="Cliff Avoidance Opportunity">
                Moving cliff-exposed property into entities domiciled in a no-tax state could restructure situs and eliminate cliff tax. Requires analysis of state sourcing rules.
              </AlertBox>
            )}
            {summary?.sec6166.eligible && (
              <AlertBox type="success" title="§6166 Installment — Available" info="§6166">
                With {fmtPct(summary.sec6166.pct)} closely held assets, the estate qualifies to pay federal tax in up to 14 annual installments, preserving liquidity during the payment period.
              </AlertBox>
            )}
            {summary && !isLowerExemption && (
              <AlertBox type="info" title="Scenario Modeling" info="OBBBA">
                Currently using {fmtM(summary.federal.exemption)} exemption (OBBBA permanent). Use the "Reduced Exemption" toggle to model exposure under a hypothetical lower exemption.
              </AlertBox>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}

// Helper for alert logic — states with estate tax
const STATE_ESTATE_TAX_CODES = ['CT','DC','HI','IL','ME','MD','MA','MN','NY','OR','RI','VT','WA'];
